import { getBffBaseUrl, getVisitKey, GUEST_ACCESS_PATH } from '@/lib/bff/config'
import { GuestsRequestError, type InputProblem } from '@/lib/bff/guests'

/*
 * BFF client for Setup › Guest data access (VIS-330): who opened guest data in
 * one tenant in a period. Landlords only (`guest_access.produce`); anyone
 * else gets `403 forbidden`, and a tenant the caller can't work in
 * `404 not_found`. Each production is recorded before anything is returned:
 * `503 access_not_recorded` means it couldn't be, and nothing was returned.
 * Errors arrive in the `{ error, problems? }` envelope; a period the server
 * refuses is `400 invalid_request` with problems on `from` or `to`.
 *
 * Times: `at` is a UTC instant, shown in the record's `timeZone`; periods are
 * calendar dates (`YYYY-MM-DD`), both inclusive, in the tenant's calendar.
 */

export type AccessPeriod = { from: string; to: string }

export type AccessActorGroup = 'venue_user' | 'visitone_staff'

export type AccessAction =
  | 'viewed_guest_record'
  | 'opened_guest_list'
  | 'searched_guests'
  | 'exported_guest_data'
  | 'showed_access_record'
  | 'downloaded_access_record'

export type AccessEvent = {
  id: string
  /** UTC instant. */
  at: string
  actor: { name?: string; email?: string; group: AccessActorGroup }
  action: AccessAction
  /** A view names the guest record it opened. */
  guestRecordId?: string
  /** A list, search or export: how many guests it included. */
  guestCount?: number
  /** Up to 100 of the included guests' source-system customerIds; the download has every one. */
  customerIds?: string[]
  moreCustomerIds?: number
  /** Included guests with no customerId, by guest record; the download has every one. */
  holderGuestIds?: string[]
  moreHolderGuestIds?: number
  /** A production of this record names the period it covered. */
  period?: AccessPeriod
}

export type AccessRecord = {
  productionId: string
  timeZone: string
  covers: string
  period: AccessPeriod
  total: number
  /** 25 events, newest first; pass `next` as `page` with the same `productionId`. */
  page: { items: AccessEvent[]; next: string | null }
}

export type AccessRecordFile = { blob: Blob; fileName: string }

/** Problem codes the server sends for a refused period. */
export type PeriodProblemCode = 'from_required' | 'to_required' | 'from_outside_retention' | 'to_in_future' | 'from_after_to'

function headers(token: string, accept: string, json = false): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'x-visit-key': getVisitKey(),
    Accept: accept,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }
}

function url(tenantId: string, path: string, query: Record<string, string> = {}) {
  const search = new URLSearchParams({ tenant: tenantId, ...query })
  return `${getBffBaseUrl('mmv')}${GUEST_ACCESS_PATH}${path}?${search}`
}

async function request(input: string, init: RequestInit): Promise<Response> {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch {
    throw new GuestsRequestError(0, null, [], 'Network error during a Guest data access request')
  }
  if (!response.ok) {
    let code: string | null = null
    let problems: InputProblem[] = []
    try {
      const body = (await response.json()) as { error?: unknown; problems?: unknown }
      if (typeof body?.error === 'string') code = body.error
      if (Array.isArray(body?.problems)) problems = body.problems as InputProblem[]
    } catch {
      code = null
    }
    throw new GuestsRequestError(response.status, code, problems, `Guest data access request failed (${response.status})`)
  }
  return response
}

async function json<T>(input: string, init: RequestInit): Promise<T> {
  const response = await request(input, init)
  try {
    return (await response.json()) as T
  } catch {
    throw new GuestsRequestError(response.status, null, [], 'Malformed Guest data access response')
  }
}

/** Produces the record for a period and returns its first page. Each call is a new, recorded production. */
export function produceRecord(token: string, tenantId: string, period: AccessPeriod) {
  return json<AccessRecord>(url(tenantId, '/record'), {
    method: 'POST',
    headers: headers(token, 'application/json', true),
    body: JSON.stringify(period),
  })
}

/** A later page of the same production; `page` is the previous page's `next`. */
export function recordPage(token: string, tenantId: string, productionId: string, page: string) {
  return json<AccessRecord>(url(tenantId, `/record/${encodeURIComponent(productionId)}`, { page }), {
    method: 'GET',
    headers: headers(token, 'application/json'),
  })
}

/**
 * The server's file name for the period. A cross-origin response doesn't
 * expose `Content-Disposition`, so the same rule names the file when the
 * header can't be read.
 */
export function recordFileName(tenantId: string, period: AccessPeriod) {
  return `guest-data-access_${tenantId.replace(/[^A-Za-z0-9_-]/g, '-')}_${period.from}_${period.to}.csv`
}

function fileNameOf(disposition: string | null): string | null {
  const match = disposition ? /filename="?([^";]+)"?/i.exec(disposition) : null
  return match ? match[1] : null
}

/** Produces the record for a period as a CSV file, recorded as a download. */
export async function downloadRecord(token: string, tenantId: string, period: AccessPeriod): Promise<AccessRecordFile> {
  const response = await request(url(tenantId, '/download'), {
    method: 'POST',
    headers: headers(token, 'text/csv', true),
    body: JSON.stringify(period),
  })
  let blob: Blob
  try {
    blob = await response.blob()
  } catch {
    throw new GuestsRequestError(response.status, null, [], 'The Guest data access download was cut short')
  }
  return { blob, fileName: fileNameOf(response.headers.get('Content-Disposition')) ?? recordFileName(tenantId, period) }
}

export const isAccessNotRecorded = (error: unknown) =>
  error instanceof GuestsRequestError && error.status === 503 && error.code === 'access_not_recorded'

/** The problem codes the server sent for one field of a refused period. */
export function periodProblems(error: unknown, field: 'from' | 'to'): PeriodProblemCode[] {
  if (!(error instanceof GuestsRequestError) || error.status !== 400) return []
  return error.problems.filter((problem) => problem.field === field).map((problem) => problem.code as PeriodProblemCode)
}
